import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';

import type { MarketAppWithTabs, WidgetV2 } from '@/features/dashboard/types';

import type { DashboardEditState } from '../dashboardEdit.types';
import { getCurrentTabDraft } from '../lib/dashboardEditValidation';
import { insertAtIndex, reorderArray } from '../lib/reorder';

type State = DashboardEditState;

const addApp: CaseReducer<
  State,
  PayloadAction<{ tabId: string; app: MarketAppWithTabs; insertAtIndex?: number }>
> = (state, action) => {
  const { tabId, app, insertAtIndex: insertIdx } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  draft.marketTabApps = insertAtIndex(draft.marketTabApps, structuredClone(app), insertIdx);
  state.isDirty = true;
};

const removeApp: CaseReducer<State, PayloadAction<{ tabId: string; appId: string }>> = (
  state,
  action
) => {
  const draft = state.tabDrafts[action.payload.tabId];
  if (!draft) return;

  draft.marketTabApps = draft.marketTabApps.filter(a => a.id !== action.payload.appId);
  state.isDirty = true;
};

const renameAppTab: CaseReducer<
  State,
  PayloadAction<{ tabId: string; appId: string; itemId: string; name: string }>
> = (state, action) => {
  const { tabId, appId, itemId, name } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  const app = draft.marketTabApps.find(a => a.id === appId);
  const tab = app?.tabs.find(t => t.id === itemId);
  if (tab) {
    tab.name = name;
    state.isDirty = true;
  }
};

const updateAppTab: CaseReducer<
  State,
  PayloadAction<{ tabId: string; appId: string; itemId: string; tab: WidgetV2 }>
> = (state, action) => {
  const { tabId, appId, itemId, tab } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  const app = draft.marketTabApps.find(a => a.id === appId);
  if (!app) return;

  const index = app.tabs.findIndex(t => t.id === itemId);
  if (index !== -1) {
    app.tabs[index] = structuredClone(tab);
    state.isDirty = true;
  }
};

const reorderApps: CaseReducer<
  State,
  PayloadAction<{ tabId: string; fromIndex: number; toIndex: number }>
> = (state, action) => {
  const { tabId, fromIndex, toIndex } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  draft.marketTabApps = reorderArray(draft.marketTabApps, fromIndex, toIndex);
  state.isDirty = true;
};

const addTabToApp: CaseReducer<
  State,
  PayloadAction<{ tabId: string; appId: string; tab: WidgetV2; insertAtIndex?: number }>
> = (state, action) => {
  const { tabId, appId, tab, insertAtIndex: insertIdx } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  const app = draft.marketTabApps.find(a => a.id === appId);
  if (!app) return;

  app.tabs = insertAtIndex(app.tabs, structuredClone(tab), insertIdx);
  state.isDirty = true;
};

const removeTabFromApp: CaseReducer<
  State,
  PayloadAction<{ tabId: string; appId: string; itemId: string }>
> = (state, action) => {
  const { tabId, appId, itemId } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  const app = draft.marketTabApps.find(a => a.id === appId);
  if (!app) return;

  app.tabs = app.tabs.filter(t => t.id !== itemId);

  if (app.tabs.length === 0) {
    draft.marketTabApps = draft.marketTabApps.filter(a => a.id !== appId);
  }

  state.isDirty = true;
};

const moveTabBetweenApps: CaseReducer<
  State,
  PayloadAction<{
    tabId: string;
    itemId: string;
    fromAppId: string;
    toAppId: string;
    insertAtIndex?: number;
    keepEmpty?: boolean;
  }>
> = (state, action) => {
  const { tabId, itemId, fromAppId, toAppId, insertAtIndex: insertIdx, keepEmpty } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;
  if (fromAppId === toAppId) return;

  const sourceApp = draft.marketTabApps.find(a => a.id === fromAppId);
  const targetApp = draft.marketTabApps.find(a => a.id === toAppId);
  if (!sourceApp || !targetApp) return;

  const tabIndex = sourceApp.tabs.findIndex(t => t.id === itemId);
  if (tabIndex === -1) return;

  const [tabToMove] = sourceApp.tabs.splice(tabIndex, 1);
  targetApp.tabs = insertAtIndex(targetApp.tabs, tabToMove, insertIdx);

  if (!keepEmpty && sourceApp.tabs.length === 0) {
    draft.marketTabApps = draft.marketTabApps.filter(a => a.id !== fromAppId);
  }

  state.isDirty = true;
};

const reorderTabsInsideApp: CaseReducer<
  State,
  PayloadAction<{ tabId: string; appId: string; fromIndex: number; toIndex: number }>
> = (state, action) => {
  const { tabId, appId, fromIndex, toIndex } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  const app = draft.marketTabApps.find(a => a.id === appId);
  if (!app) return;

  app.tabs = reorderArray(app.tabs, fromIndex, toIndex);
  state.isDirty = true;
};

const updateCurrentTabApps: CaseReducer<State, PayloadAction<{ apps: MarketAppWithTabs[] }>> = (
  state,
  action
) => {
  const draft = getCurrentTabDraft(state);
  if (!draft) return;

  draft.marketTabApps = action.payload.apps.map(app => structuredClone(app));
  state.isDirty = true;
};

const removeEmptyApps: CaseReducer<State> = state => {
  const draft = getCurrentTabDraft(state);
  if (!draft) return;

  const before = draft.marketTabApps.length;
  draft.marketTabApps = draft.marketTabApps.filter(app => app.tabs.length > 0);
  if (draft.marketTabApps.length !== before) {
    state.isDirty = true;
  }
};

export const appReducers = {
  addApp,
  removeApp,
  renameAppTab,
  updateAppTab,
  reorderApps,
  addTabToApp,
  removeTabFromApp,
  moveTabBetweenApps,
  reorderTabsInsideApp,
  updateCurrentTabApps,
  removeEmptyApps
};
